import { useContext, useEffect, useMemo, useState } from 'react';
import { Card, Divider, Skeleton, Tooltip } from 'antd';
import { DollarOutlined, FileTextOutlined, GiftOutlined, TrophyOutlined } from '@ant-design/icons';
import { IDashboardTopUserItem } from '../../../../services/http/mappers/DashboardMapper';
import { ThemeContext } from '../../../../contexts/ThemeContext';
import { formatCpf } from '../../../../utils/Masks';
import { convertMoedaBRDoJSNovo, poeValorComPontosEmMil } from '../../../../utils/Conversores';

interface TopUsersListProps {
    data: IDashboardTopUserItem[];
    isLoading: boolean;
    typeSelected: string;
    onTypeChange: (type: string) => void;
    sortBy?: 'total' | 'tickets' | 'value';
    onSortByChange?: (sortBy: 'total' | 'tickets' | 'value') => void;
}

const TYPE_NOTA: { key: string; label: string }[] = [
    { key: 'todas', label: 'Todas' },
    { key: 'NFC', label: 'NFC' },
    { key: 'NFE', label: 'NFE' },
    { key: 'NFS', label: 'NFS' },
];

const rankColors = ['#FFD700', '#C0C0C0', '#CD7F32'];

function maskCpfPartial(cpf: string) {
    if (!cpf) return '';
    const digits = cpf.replace(/\D/g, '');
    if (digits.length === 11) {
        return `${digits.slice(0, 3)}.***.***-${digits.slice(9)}`;
    }
    return formatCpf(cpf);
}

const MiniStat = ({
    label,
    value,
    color,
    width = 70,
}: {
    label: string;
    value: string | number;
    color: string;
    width?: number;
}) => (
    <div
        style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            width: width,
            minWidth: width,
            flexShrink: 0,
            textAlign: "center",
        }}
    >
        <div
            style={{
                fontSize: 10,
                fontWeight: 600,
                lineHeight: 1,
                color: "#888",
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                whiteSpace: "nowrap",
            }}
        >
            {label}
        </div>

        <div
            style={{
                marginTop: 3,
                fontSize: 15,
                fontWeight: 700,
                color: color,
                lineHeight: 1.1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: width,
            }}
        >
            {value}
        </div>
    </div>
);

const TopUsersSkeleton = ({ isDark, isValueSort }: { isDark: boolean; isValueSort?: boolean }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[1, 2, 3, 4, 5].map(key => (
            <div
                key={key}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 72,
                    borderRadius: 12,
                    background: isDark ? '#141414' : '#fafafa',
                    border: `1px solid ${isDark ? '#2a2a2a' : '#ececec'}`,
                    padding: '12px 20px',
                    gap: 16,
                    overflow: 'hidden',
                }}
            >
                <Skeleton.Avatar active size={44} shape="circle" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <Skeleton.Input active size="small" style={{ width: '60%', height: 16, borderRadius: 4 }} />
                    <Skeleton.Input active size="small" style={{ width: '40%', height: 12, borderRadius: 4 }} />
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    <Skeleton.Button active size="small" style={{ width: 65, height: 26, borderRadius: 4 }} />
                    <Skeleton.Button active size="small" style={{ width: 70, height: 26, borderRadius: 4 }} />
                    <Skeleton.Button active size="small" style={{ width: 115, height: 26, borderRadius: 4 }} />
                </div>
                <Skeleton.Button active size="large" style={{ width: isValueSort ? 160 : 135, height: 48, borderRadius: 8, flexShrink: 0 }} />
            </div>
        ))}
    </div>
);

export default function TopUsersList({
    data,
    isLoading,
    typeSelected,
    onTypeChange,
    sortBy: externalSortBy,
    onSortByChange,
}: TopUsersListProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [internalSortBy, setInternalSortBy] = useState<'total' | 'tickets' | 'value'>('total');

    const sortBy = externalSortBy ?? internalSortBy;

    const handleSortChange = (newSort: 'total' | 'tickets' | 'value') => {
        if (onSortByChange) {
            onSortByChange(newSort);
        } else {
            setInternalSortBy(newSort);
        }
    };

    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, []);

    const sortedData = useMemo(() => {
        if (!data || !Array.isArray(data)) return [];
        const list = [...data];
        if (sortBy === 'tickets') {
            return list.sort((a, b) => (b.totalTickets || 0) - (a.totalTickets || 0));
        }
        if (sortBy === 'value') {
            return list.sort((a, b) => (b.totalValue || 0) - (a.totalValue || 0));
        }
        return list.sort((a, b) => (b.total || 0) - (a.total || 0));
    }, [data, sortBy]);

    const highlightConfig = useMemo(() => {
        switch (sortBy) {
            case 'tickets':
                return {
                    label: 'Bilhetes',
                    color: '#722ed1',
                    getValue: (item: IDashboardTopUserItem) => poeValorComPontosEmMil(item.totalTickets),
                };
            case 'value':
                return {
                    label: 'Valor Total',
                    color: '#08979c',
                    getValue: (item: IDashboardTopUserItem) => `R$ ${convertMoedaBRDoJSNovo(item.totalValue)}`,
                };
            default:
                return {
                    label: 'Total de Notas',
                    color: '#1677ff',
                    getValue: (item: IDashboardTopUserItem) => poeValorComPontosEmMil(item.total),
                };
        }
    }, [sortBy]);

    const subtitleText = (() => {
        if (sortBy === 'tickets') return 'Ranking por quantidade de bilhetes gerados';
        if (sortBy === 'value') return 'Ranking por valor total em notas fiscais';
        return 'Ranking por total de notas importadas';
    })();

    const renderSubStats = (item: IDashboardTopUserItem) => {
        if (sortBy === 'tickets') {
            return (
                <>
                    <MiniStat
                        label="Total Notas"
                        value={poeValorComPontosEmMil(item.total)}
                        color="#1677ff"
                        width={70}
                    />
                    <Divider type="vertical" style={{ height: 36, margin: "0 2px" }} />
                    <MiniStat
                        label="Válidas"
                        value={poeValorComPontosEmMil(item.totalValid)}
                        color="#389e0d"
                        width={65}
                    />
                    <Divider type="vertical" style={{ height: 36, margin: "0 2px" }} />
                    <MiniStat
                        label="Valor"
                        value={`R$ ${convertMoedaBRDoJSNovo(item.totalValue)}`}
                        color="#08979c"
                        width={115}
                    />
                </>
            );
        }

        if (sortBy === 'value') {
            return (
                <>
                    <MiniStat
                        label="Total Notas"
                        value={poeValorComPontosEmMil(item.total)}
                        color="#1677ff"
                        width={70}
                    />
                    <Divider type="vertical" style={{ height: 36, margin: "0 2px" }} />
                    <MiniStat
                        label="Válidas"
                        value={poeValorComPontosEmMil(item.totalValid)}
                        color="#389e0d"
                        width={65}
                    />
                    <Divider type="vertical" style={{ height: 36, margin: "0 2px" }} />
                    <MiniStat
                        label="Bilhetes"
                        value={poeValorComPontosEmMil(item.totalTickets)}
                        color="#722ed1"
                        width={70}
                    />
                </>
            );
        }

        return (
            <>
                <MiniStat
                    label="Válidas"
                    value={poeValorComPontosEmMil(item.totalValid)}
                    color="#389e0d"
                    width={65}
                />
                <Divider type="vertical" style={{ height: 36, margin: "0 2px" }} />
                <MiniStat
                    label="Bilhetes"
                    value={poeValorComPontosEmMil(item.totalTickets)}
                    color="#722ed1"
                    width={70}
                />
                <Divider type="vertical" style={{ height: 36, margin: "0 2px" }} />
                <MiniStat
                    label="Valor"
                    value={`R$ ${convertMoedaBRDoJSNovo(item.totalValue)}`}
                    color="#08979c"
                    width={115}
                />
            </>
        );
    };

    return (
        <Card
            title={
                <div style={{ paddingTop: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 20 }}>Top Cidadãos Engajados</div>
                    <div style={{ fontWeight: 400, fontSize: 18, color: '#888' }}>{subtitleText}</div>
                </div>
            }
            extra={
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        alignItems: isMobile ? "flex-start" : "flex-end",
                    }}
                >
                    <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12, color: "#888", fontWeight: 500, marginRight: 2 }}>Ranking por:</span>
                        <button
                            type="button"
                            onClick={() => handleSortChange('total')}
                            style={{
                                padding: '3px 8px',
                                borderRadius: 6,
                                border: '1px solid',
                                borderColor: sortBy === 'total' ? '#1677ff' : (isDark ? '#434343' : '#d9d9d9'),
                                background: sortBy === 'total' ? '#1677ff' : (isDark ? '#1f1f1f' : '#fff'),
                                color: sortBy === 'total' ? '#fff' : (isDark ? '#aaa' : '#555'),
                                fontSize: 12,
                                fontWeight: sortBy === 'total' ? 600 : 400,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                            }}
                        >
                            <FileTextOutlined style={{ fontSize: 11 }} />
                            <span>Notas</span>
                        </button>

                        <button
                            type="button"
                            onClick={() => handleSortChange('tickets')}
                            style={{
                                padding: '3px 8px',
                                borderRadius: 6,
                                border: '1px solid',
                                borderColor: sortBy === 'tickets' ? '#722ed1' : (isDark ? '#434343' : '#d9d9d9'),
                                background: sortBy === 'tickets' ? '#722ed1' : (isDark ? '#1f1f1f' : '#fff'),
                                color: sortBy === 'tickets' ? '#fff' : (isDark ? '#aaa' : '#555'),
                                fontSize: 12,
                                fontWeight: sortBy === 'tickets' ? 600 : 400,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                            }}
                        >
                            <GiftOutlined style={{ fontSize: 11 }} />
                            <span>Bilhetes</span>
                        </button>

                        <button
                            type="button"
                            onClick={() => handleSortChange('value')}
                            style={{
                                padding: '3px 8px',
                                borderRadius: 6,
                                border: '1px solid',
                                borderColor: sortBy === 'value' ? '#08979c' : (isDark ? '#434343' : '#d9d9d9'),
                                background: sortBy === 'value' ? '#08979c' : (isDark ? '#1f1f1f' : '#fff'),
                                color: sortBy === 'value' ? '#fff' : (isDark ? '#aaa' : '#555'),
                                fontSize: 12,
                                fontWeight: sortBy === 'value' ? 600 : 400,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                            }}
                        >
                            <DollarOutlined style={{ fontSize: 11 }} />
                            <span>Valor</span>
                        </button>
                    </div>

                    <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12, color: "#888", fontWeight: 500, marginRight: 2 }}>Tipo:</span>
                        {TYPE_NOTA.map(({ key, label }) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => onTypeChange(key)}
                                style={{
                                    padding: '3px 8px',
                                    borderRadius: 6,
                                    border: '1px solid',
                                    borderColor: typeSelected === key ? '#1677ff' : (isDark ? '#434343' : '#d9d9d9'),
                                    background: typeSelected === key ? '#1677ff' : (isDark ? '#1f1f1f' : '#fff'),
                                    color: typeSelected === key ? '#fff' : (isDark ? '#aaa' : '#555'),
                                    fontSize: 12,
                                    fontWeight: typeSelected === key ? 600 : 400,
                                    cursor: 'pointer',
                                }}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
            }
            style={{
                background: isDark ? '#1f1f1f' : '#FFFFFF',
                borderRadius: 16,
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                border: 'none',
                flex: 1,
            }}
            bodyStyle={{ padding: '8px 20px 20px' }}
        >
            {isLoading ? (
                <TopUsersSkeleton isDark={isDark} isValueSort={sortBy === 'value'} />
            ) : sortedData.length === 0 ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, color: '#888' }}>
                    Nenhum dado disponível
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 480, overflowY: 'auto', paddingRight: 4 }}>
                    {sortedData.map((item, index) => {
                        const rankColor = rankColors[index] ?? (isDark ? '#444' : '#e8e8e8');
                        const isTopThree = index < 3;
                        const isValueSort = sortBy === 'value';
                        const rightBoxWidth = isValueSort ? 165 : 135;
                        const val = highlightConfig.getValue(item);
                        const isCurrency = isValueSort || (typeof val === 'string' && val.startsWith('R$'));
                        const valFontSize = isCurrency ? (val.length > 13 ? 15 : 17) : 20;

                        return (
                            <div
                                key={item.userId || item.cpf || index}
                                style={{
                                    display: 'flex',
                                    flexDirection: isMobile ? "column" : "row",
                                    alignItems: "stretch",
                                    minHeight: isMobile ? 'auto' : 72,
                                    borderRadius: 12,
                                    background: isDark ? '#141414' : '#fafafa',
                                    border: `1px solid ${isDark ? '#2a2a2a' : '#ececec'}`,
                                    overflow: 'hidden',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flex: 1,
                                        minWidth: 0,
                                        flexDirection: isMobile ? "column" : "row",
                                        alignItems: "center",
                                        gap: 16,
                                        padding: isMobile ? '14px 16px' : '12px 20px',
                                    }}
                                >
                                    {!isMobile && (
                                        <div
                                            style={{
                                                width: 44,
                                                height: 44,
                                                minWidth: 44,
                                                borderRadius: '50%',
                                                background: isTopThree ? rankColor : (isDark ? '#2a2a2a' : '#e8e8e8'),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 700,
                                                fontSize: isTopThree ? 22 : 18,
                                                color: isTopThree ? (index === 0 ? '#7a5800' : '#555') : (isDark ? '#aaa' : '#666'),
                                                flexShrink: 0,
                                            }}
                                        >
                                            {isTopThree ? <TrophyOutlined /> : index + 1}
                                        </div>
                                    )}

                                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <Tooltip title={item.name}>
                                            <div
                                                style={{
                                                    fontWeight: 600,
                                                    fontSize: isMobile ? 15 : 17,
                                                    lineHeight: 1.3,
                                                    color: isDark ? "#e8e8e8" : "#222",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}
                                            >
                                                {item.name || 'Cidadão não identificado'}
                                            </div>
                                        </Tooltip>

                                        <div
                                            style={{
                                                fontSize: 13,
                                                lineHeight: 1.3,
                                                color: "#888",
                                                marginTop: 2,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                            }}
                                        >
                                            <span>{maskCpfPartial(item.cpf)}</span>
                                            {item.email && (
                                                <span
                                                    style={{
                                                        color: '#aaa',
                                                        fontSize: 12,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    • {item.email}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div
                                        style={{
                                            display: "flex",
                                            gap: 8,
                                            flexShrink: 0,
                                            alignItems: "center",
                                        }}
                                    >
                                        {renderSubStats(item)}
                                    </div>
                                </div>

                                <div
                                    style={{
                                        alignSelf: 'stretch',
                                        background: isDark ? '#1a1a1a' : '#f5f5f5',
                                        borderLeft: isMobile ? 'none' : `1px solid ${isDark ? '#2a2a2a' : '#ececec'}`,
                                        borderTop: isMobile ? `1px solid ${isDark ? '#2a2a2a' : '#ececec'}` : 'none',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: isMobile ? '12px 14px' : '0 10px',
                                        width: isMobile ? 'auto' : rightBoxWidth,
                                        minWidth: isMobile ? 'auto' : rightBoxWidth,
                                        maxWidth: isMobile ? 'auto' : rightBoxWidth,
                                        flexShrink: 0,
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: 10,
                                            fontWeight: 600,
                                            lineHeight: 1,
                                            color: '#888',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.4px',
                                            whiteSpace: 'nowrap',
                                            textAlign: 'center',
                                        }}
                                    >
                                        {highlightConfig.label}
                                    </div>
                                    <div
                                        style={{
                                            marginTop: 4,
                                            fontSize: valFontSize,
                                            fontWeight: 700,
                                            color: isDark ? '#e8e8e8' : highlightConfig.color,
                                            lineHeight: 1.1,
                                            textAlign: 'center',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: '100%',
                                        }}
                                    >
                                        {val}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </Card>
    );
}
